import type { HTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { CircularProgress } from './Progress'

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  icon: string
  title: string
  description?: string | undefined
  action?: ReactNode | undefined
  /** Versione ridotta, per l'interno di una cella di tabella. */
  compact?: boolean | undefined
}

export function EmptyState({ icon, title, description, action, compact, className, ...rest }: EmptyStateProps) {
  return (
    <div {...rest} className={cx('md-empty', compact && 'md-empty--compact', className)}>
      <Icon name={icon} size={40} className="md-empty__icon" />
      <p className="md-empty__title">{title}</p>
      {description ? <p className="md-empty__description">{description}</p> : null}
      {action ? <div className="md-empty__action">{action}</div> : null}
    </div>
  )
}

export interface LoadingStateProps extends HTMLAttributes<HTMLDivElement> {
  label?: string | undefined
}

export function LoadingState({ label = 'Caricamento…', className, ...rest }: LoadingStateProps) {
  return (
    <div role="status" {...rest} className={cx('md-loading', className)}>
      <CircularProgress label={label} />
      <span>{label}</span>
    </div>
  )
}

export type CalloutTone = 'info' | 'success' | 'warning' | 'error'

const CALLOUT_ICON: Record<CalloutTone, string> = {
  info: 'info',
  success: 'check_circle',
  warning: 'warning',
  error: 'error',
}

export interface CalloutProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tone: CalloutTone
  icon?: string | undefined
  title?: string | undefined
  action?: ReactNode | undefined
}

/** Sostituisce `alert alert-*`: coppia container/on-container del tono. */
export function Callout({ tone, icon, title, action, className, children, ...rest }: CalloutProps) {
  return (
    <div
      role={tone === 'error' ? 'alert' : undefined}
      {...rest}
      className={cx('md-callout', `md-callout--${tone}`, className)}
    >
      <Icon name={icon ?? CALLOUT_ICON[tone]} size={24} className="md-callout__icon" />
      <div className="md-callout__text">
        {title ? <p className="md-callout__title">{title}</p> : null}
        <div>{children}</div>
      </div>
      {action ? <div className="md-callout__action">{action}</div> : null}
    </div>
  )
}

export interface AccessDeniedProps {
  /** Cosa serve per vedere la sezione (es. "il permesso Contratti: lettura"). */
  requirement?: string | undefined
}

export function AccessDenied({ requirement }: AccessDeniedProps) {
  return (
    <EmptyState
      role="alert"
      icon="lock"
      title="Accesso non autorizzato"
      description={requirement ? `Questa sezione richiede ${requirement}.` : 'Non hai i permessi per visualizzare questa sezione.'}
    />
  )
}
